import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Heart, 
  MessageCircle, 
  Clock,
  Loader,
  Search,
  X,
  Send,
  User,
  ChevronDown,
  Palette,
  Award,
  User as UserIcon,
  Mail,
  Menu,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './BlogSection.css';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsToShow, setItemsToShow] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [commentForm, setCommentForm] = useState({ name: '', comment: '' });
  const [allTags, setAllTags] = useState([]);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();

  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      setViewport({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/blogs`);
      setBlogs(response.data);
      
      const tags = new Set();
      response.data.forEach(blog => {
        blog.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(['all', ...Array.from(tags)]);
      
      filterBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = useCallback((blogList = blogs) => {
    let filtered = blogList;
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (selectedTag !== 'all') {
      filtered = filtered.filter(blog => blog.tags?.includes(selectedTag));
    }
    
    setDisplayedBlogs(filtered.slice(0, itemsToShow));
  }, [blogs, searchQuery, selectedTag, itemsToShow]);

  useEffect(() => {
    filterBlogs();
  }, [searchQuery, selectedTag, itemsToShow, filterBlogs]);

  const handleLike = async (blogId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/blogs/${blogId}/like`);
      setBlogs(prev => prev.map(blog =>
        blog._id === blogId ? { ...blog, likes: (blog.likes || 0) + 1 } : blog
      ));
    } catch (error) {
      console.error('Error liking blog:', error);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.comment.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/blogs/${selectedBlog._id}/comment`, commentForm);
      setCommentForm({ name: '', comment: '' });
      setShowCommentModal(false);
      fetchBlogs();
      alert('Comment added successfully!');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment. Please try again.');
    }
  };

  const handleShowMore = () => {
    setShowMoreLoading(true);
    setTimeout(() => {
      setItemsToShow(prev => prev + (viewport.isMobile ? 3 : 5));
      setShowMoreLoading(false);
    }, 500);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const plainText = content.replace(/<[^>]*>/g, '');
    const words = plainText.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Get grid class based on number of images
  const getImagesGridClass = (imageCount) => {
    if (imageCount === 1) return 'single';
    if (imageCount === 2) return 'double';
    return 'grid';
  };

  if (loading) {
    return (
      <div className="blog-loading">
        <Loader className="spinner" size={48} />
        <p>Loading blog posts...</p>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {/* Navigation Header */}
      <header className="blog-header-nav">
        <div className="header-container">
          <div className="header-content">
            <div className="brand" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="My Musing Logo" className="brand-logo" />
              <div className="brand-text">
                <h1>MY MUSING</h1>
                <p>ART AND CREATIONS</p>
              </div>
            </div>

            <nav className={`main-nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
              <button onClick={() => navigate('/')} className="nav-link" type="button">
                <Home size={18} />
                <span>Home</span>
              </button>
              
              <button onClick={() => navigate('/blog')} className="nav-link active" type="button">
                <MessageCircle size={18} />
                <span>Blog</span>
              </button>
              
              <button onClick={() => { navigate('/artist-login'); }} className="nav-link login-btn" type="button">
               <User size={18} />
               <span>Artist Portal</span>
              </button>
            </nav>

            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <div className="blog-section">
        <div className="blog-container">
          <div className="blog-header">
            <h1 className="blog-title">
              Creative Journal
              <span className="title-accent">({blogs.length} posts)</span>
            </h1>
            <p className="blog-subtitle">
              Insights, stories, and inspiration from an artist's perspective
            </p>
          </div>

          <div className="blog-filters">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search" onClick={() => setSearchQuery('')}>
                  <X size={16} />
                </button>
              )}
            </div>

          
          </div>

          {displayedBlogs.length === 0 ? (
            <div className="no-blogs">
              <MessageCircle size={64} />
              <h3>No blog posts found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div className="blog-feed">
                {displayedBlogs.map((blog, index) => (
                  <article key={blog._id} className="blog-post" style={{ animationDelay: `${index * 0.05}s` }}>
                    <div className="post-header">
                      <div className="post-author">
                        <div className="author-avatar">DC</div>
                        <div className="author-info">
                          <h3>Debraj Chatterjee</h3>
                          <div className="post-meta">
                            <Calendar size={12} />
                            <span>{formatDate(blog.createdAt)}</span>
                            <span className="dot">•</span>
                            <Clock size={12} />
                            <span>{getReadingTime(blog.content)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="post-body">
                      <h2 className="post-title">{blog.title}</h2>
                      <div className="post-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

                      {blog.images && blog.images.length > 0 && (
                        <div className={`post-images ${getImagesGridClass(blog.images.length)}`}>
                          {blog.images.map((img, idx) => (
                            <div key={idx} className="post-image">
                              <img src={img.url} alt={img.caption || `Image ${idx + 1}`} />
                            </div>
                          ))}
                        </div>
                      )}

                      {blog.tags && blog.tags.length > 0 && (
                        <div className="post-tags">
                          {blog.tags.map(tag => (
                            <span key={tag} className="post-tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="post-footer">
                      <div className="post-actions">
                        <button className="action-btn" onClick={(e) => handleLike(blog._id, e)}>
                          <Heart size={20} fill={(blog.likes || 0) > 0 ? '#ff6b6b' : 'none'} stroke={(blog.likes || 0) > 0 ? '#ff6b6b' : 'currentColor'} />
                          <span>{blog.likes || 0} {(blog.likes || 0) === 1 ? 'Like' : 'Likes'}</span>
                        </button>
                        <button className="action-btn" onClick={() => { setSelectedBlog(blog); setShowCommentModal(true); }}>
                          <MessageCircle size={20} />
                          <span>{blog.comments?.length || 0} {(blog.comments?.length || 0) === 1 ? 'Comment' : 'Comments'}</span>
                        </button>
                      </div>
                    </div>

                    {/* {blog.comments && blog.comments.length > 0 && (
                      <div className="post-comments">
                        {blog.comments.slice(0, 3).map((comment, idx) => (
                          <div key={idx} className="comment">
                            <div className="comment-avatar">{comment.name.charAt(0).toUpperCase()}</div>
                            <div className="comment-content">
                              <div className="comment-header">
                                <strong>{comment.name}</strong>
                                <span className="comment-date">{formatDate(comment.createdAt)}</span>
                              </div>
                              <p>{comment.comment}</p>
                            </div>
                          </div>
                        ))}
                        {blog.comments.length > 3 && (
                          <button className="view-more-comments" onClick={() => { setSelectedBlog(blog); setShowCommentModal(true); }}>
                            View all {blog.comments.length} comments
                          </button>
                        )}
                      </div>
                    )} */}
                  </article>
                ))}
              </div>

              {displayedBlogs.length < blogs.filter(b => {
                let matches = true;
                if (searchQuery.trim()) {
                  matches = matches && (b.title.toLowerCase().includes(searchQuery.toLowerCase()) || b.content.toLowerCase().includes(searchQuery.toLowerCase()));
                }
                if (selectedTag !== 'all') {
                  matches = matches && b.tags?.includes(selectedTag);
                }
                return matches;
              }).length && (
                <div className="show-more-container">
                  <button className="show-more-btn" onClick={handleShowMore} disabled={showMoreLoading}>
                    {showMoreLoading ? (
                      <>
                        <Loader className="spinner" size={20} />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown size={20} />
                        <span>Load More Posts</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showCommentModal && selectedBlog && (
        <div className="modal-overlay" onClick={() => setShowCommentModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCommentModal(false)}>
              <X size={24} />
            </button>

            <div className="modal-header">
              <h3>Comments ({selectedBlog.comments?.length || 0})</h3>
              <p>{selectedBlog.title}</p>
            </div>

            <div className="modal-body">
              {selectedBlog.comments && selectedBlog.comments.length > 0 ? (
                <div className="comments-list">
                  {selectedBlog.comments.map((comment, index) => (
                    <div key={index} className="comment-item">
                      <div className="comment-avatar">{comment.name.charAt(0).toUpperCase()}</div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <strong>{comment.name}</strong>
                          <span className="comment-date">{formatDate(comment.createdAt)}</span>
                        </div>
                        <p>{comment.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-comments">
                  <MessageCircle size={48} />
                  <p>No comments yet. Be the first!</p>
                </div>
              )}
            </div>

            <form onSubmit={handleComment} className="modal-form">
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  required
                  value={commentForm.name}
                  onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                  placeholder="Enter your name"
                />
              </div>

              <div className="form-group">
                <label>Your Comment *</label>
                <textarea
                  required
                  rows="4"
                  value={commentForm.comment}
                  onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })}
                  placeholder="Share your thoughts..."
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Send size={18} />
                  <span>Post Comment</span>
                </button>
                <button type="button" onClick={() => setShowCommentModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogSection;